import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSelectModule } from "@angular/material/select";
import { MatOptionModule } from "@angular/material/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatChipsModule } from "@angular/material/chips";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatCardModule } from "@angular/material/card";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";

import { IPropertyResponse } from "../../interfaces/i-property-response";
import { IPropertyStructure } from "../../interfaces/i-property-structure";
import { IFootprintVisibility } from "../../interfaces/i-footprint-visibility";
import { IFeatureChip } from "../../interfaces/i-feature-chip";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { StructureCardComponent } from "./structure-card/structure-card.component";
import { DocumentService } from "../../services/internal-services/document.service";
import { buildDetectedChips } from "../../utils/feature-chips.util";
import {formatArea  } from "../../utils/format-utils";
import { PROPERTY_FEATURE_CHIP_LABELS } from "../../config/property-feature-chip-labels.config";
import { MatTooltip } from "@angular/material/tooltip";

@Component({
  selector: "lib-property-overview",
  standalone: true,
  templateUrl: "./property-overview.component.html",
  styleUrls: ["./property-overview.component.scss"],
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    MatOptionModule,
    MatExpansionModule,
    MatChipsModule,
    MatProgressBarModule,
    MatCardModule,
    StructureCardComponent,
    MatButton,
    MatIconButton,
    MatTooltip,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
  ],
})
export class PropertyOverviewComponent {
  @Input() set propertyData(value: IPropertyResponse) {
    this._propertyData = value;
    this.buildStructures(value);
  }

  get propertyData(): IPropertyResponse {
    return this._propertyData;
  }

  @Input() measurementSystem: number;

  @Input() visibilityValues: IFootprintVisibility = { propertyFootprint: true, labelsFootprint: true };

  @Input() discreteEnabled = false;

  @Output() measurementSystemChange = new EventEmitter<number>();

  @Output() visibilityChange = new EventEmitter<IFootprintVisibility>();

  /** Emits when the user clicks a zoom button, carrying the WKT geometry and padding. */
  @Output() zoomRequested = new EventEmitter<{ wkt: string; paddingPx: number }>();

  readonly measurementSystems = ViewerConstants.MEASUREMENT_SYSTEMS.IDS;

  structures: IPropertyStructure[] = [];

  expandedPropertyFeatures = true;

  showAllPropertyFeatures = false;

  private _propertyData: IPropertyResponse;

  /** Sorts structures: primary first, then others by footprint area descending. */
  private buildStructures(data: IPropertyResponse): void {
    if (!data) return;
    const raw = data.structures ?? data.structure ?? [];
    const primary = raw.filter((s) => s.primaryStructure);
    const others = raw
      .filter((s) => !s.primaryStructure)
      .sort((a, b) => (b.footPrint?.area ?? 0) - (a.footPrint?.area ?? 0));
    this.structures = [...primary, ...others];
  }

  calculateTotalArea(): number {
    return this.structures.reduce((acc, s) => acc + (s.footPrint?.area ?? 0), 0);
  }

  /** Formats an area from square metres to the user preferred unit string. */
  formatArea(m2: number): string {
    return formatArea(m2, this.measurementSystem === this.measurementSystems.IMPERIAL);
  }

  onMeasurementSystemChange(value: number): void {
    this.measurementSystemChange.emit(value);
  }

  onVisibilityChange(key: keyof IFootprintVisibility, checked: boolean): void {
    this.visibilityChange.emit({ ...this.visibilityValues, [key]: checked });
  }

  /** Forwards zoom events emitted by child structure cards. */
  onStructureZoom(event: { wkt: string; paddingPx: number }): void {
    this.zoomRequested.emit(event);
  }

  /** Copies the given value to the clipboard. */
  copyToClipboard(value: string): void {
    DocumentService.copyTextToClipboard(value);
  }

  /** Zooms to the property parcel boundary. */
  onParcelZoom(): void {
    const wkt = this.propertyData?.property_features?.parcel_wkt;
    if (!wkt?.trim()) return;
    this.zoomRequested.emit({ wkt, paddingPx: 100 });
  }

  /**
   * Builds IFeatureChip[] from property_features using buildDetectedChips().
   * Label config is driven by PROPERTY_FEATURE_CHIP_LABELS.
   * Pool and vehicle subtypes are auto-expanded; parent entries are skipped.
   */
  getPropertyFeatures(): IFeatureChip[] {
    const features = this.propertyData?.property_features;
    if (!features) return [];
    return buildDetectedChips(features, "", PROPERTY_FEATURE_CHIP_LABELS, this.formatArea.bind(this))
      .filter((c) => this.showAllPropertyFeatures || c.detected)
      .sort((a, b) => Number(b.detected) - Number(a.detected));
  }
}
